import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, LogOut, ShieldCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, useApi, useRefresh } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS = { trialing: "Prova gratuita in corso", active: "Attivo", past_due: "Pagamento non riuscito", unpaid: "Non pagato", canceled: "Disdetto", comp: "Accesso omaggio" };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("it-IT") : "—");

const AGENT_FIELDS = [["business_name", "Ragione sociale / Nome e cognome *"], ["vat_number", "P.IVA / Codice fiscale *"], ["business_address", "Sede legale *"],
  ["oam_number", "N. iscrizione OAM"], ["privacy_email", "Email / PEC per la privacy"]];

const AgentData = ({ f, setF }) => (
  <div className="space-y-2" data-testid="dpa-agent-data">
    <p className="text-sm font-medium">I tuoi dati come Titolare del trattamento (servono per il tuo DPA)</p>
    <div className="grid sm:grid-cols-2 gap-2">
      {AGENT_FIELDS.map(([k, l]) => <Input key={k} data-testid={`dpa-${k}`} placeholder={l} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />)}
    </div>
  </div>
);

const PlanPicker = ({ st, onDone }) => {
  const [plan, setPlan] = useState("yearly");
  const [terms, setTerms] = useState(st.terms_accepted);
  const [dpa, setDpa] = useState(st.terms_accepted);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  const dataOk = st.terms_accepted || (f.business_name?.trim() && f.vat_number?.trim() && f.business_address?.trim());
  const go = async (provider = "stripe") => {
    setBusy(provider);
    try {
      if (!st.terms_accepted) await api.post("/billing/accept", { terms, dpa, ...f });
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); onDone(); }
  };
  const trial = !st.had_trial;
  return (
    <div className="space-y-6" data-testid="plan-picker">
      <div className="grid sm:grid-cols-2 gap-4">
        {[["monthly", "Mensile", st.prices.monthly, "/mese"], ["yearly", "Annuale", st.prices.yearly, "/anno · 2 mesi gratis"]].map(([id, t, p, u]) => (
          <button key={id} data-testid={`plan-${id}`} onClick={() => setPlan(id)} className={`text-left rounded-2xl border-2 p-6 transition-colors ${plan === id ? "border-sky-500 bg-sky-50/50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
            <p className="font-display font-semibold">{t}</p>
            <p className="mt-2"><span className="font-display text-3xl font-bold">€{p}</span> <span className="text-sm text-slate-500">{u} + IVA</span></p>
          </button>
        ))}
      </div>
      {!st.terms_accepted && (
        <div className="space-y-3 rounded-xl bg-white border border-slate-200 p-5">
          <AgentData f={f} setF={setF} />
          <label className="flex items-start gap-3 text-sm"><Checkbox data-testid="accept-terms" checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5" /><span>Ho letto e accetto i <a href="https://www.iubenda.com/termini-e-condizioni/64612498" target="_blank" rel="noreferrer" data-testid="subscribe-terms-link" className="text-sky-700 underline">Termini e Condizioni</a> e ho letto la <a href="https://www.iubenda.com/privacy-policy/64612498" target="_blank" rel="noreferrer" data-testid="subscribe-privacy-link" className="text-sky-700 underline">Privacy Policy</a>.</span></label>
          <label className="flex items-start gap-3 text-sm"><Checkbox data-testid="accept-dpa" checked={dpa} onCheckedChange={(v) => setDpa(!!v)} className="mt-0.5" /><span>Accetto l'<Link to="/dpa" target="_blank" className="text-sky-700 underline">Accordo sul trattamento dei dati (art. 28 GDPR)</Link>: Kreditofacile tratta i dati dei miei clienti come responsabile per mio conto.</span></label>
        </div>
      )}
      <Button data-testid="start-checkout-btn" onClick={() => go("stripe")} disabled={!!busy || !terms || !dpa || !dataOk} className="w-full h-12 rounded-full bg-[#0F172A] hover:bg-sky-600 text-base">
        <CreditCard className="h-4 w-4 mr-2" />{busy === "stripe" ? "Apertura pagamento…" : trial ? "Inizia la prova gratuita con carta" : "Attiva l'abbonamento con carta"}
      </Button>
      <p className="text-xs text-slate-500 text-center">{trial ? "Nessun addebito per 30 giorni: puoi disdire prima della fine della prova senza pagare nulla." : "La prova gratuita è già stata utilizzata: l'addebito avviene subito."} Pagamento sicuro con Stripe.</p>
    </div>
  );
};

const Manage = ({ st }) => {
  const portal = async () => {
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-6 space-y-3" data-testid="subscription-manage">
      <p className="flex items-center gap-2 font-display font-semibold"><Check className="h-5 w-5 text-emerald-600" />{STATUS[st.status] || st.status}</p>
      {st.plan && <p className="text-sm text-slate-600">Piano {st.plan === "yearly" ? "annuale (€690 + IVA)" : "mensile (€69 + IVA)"}</p>}
      {st.status === "trialing" && <p className="text-sm text-slate-600" data-testid="trial-end">Prova gratuita fino al <b>{fmt(st.trial_end)}</b>: il primo addebito avverrà in quella data.</p>}
      {st.status === "active" && <p className="text-sm text-slate-600">{st.cancel_at_period_end ? "Disdetto: resta attivo fino al" : "Prossimo rinnovo il"} <b>{fmt(st.current_period_end)}</b></p>}
      {st.has_customer && <Button data-testid="open-portal-btn" variant="outline" onClick={portal}>Gestisci carta, fatture o disdetta</Button>}
    </div>
  );
};

export default function Subscribe() {
  const { data: st, refetch } = useApi("/billing/status");
  const { user, logout } = useAuth();
  const refresh = useRefresh();
  if (!st) return <div className="min-h-screen grid place-items-center text-slate-400">Caricamento…</div>;
  const ok = ["active", "admin"].includes(st.access);
  const lapsed = ["readonly", "blocked"].includes(st.access);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="subscribe-page">
      <header className="flex items-center justify-between px-6 lg:px-12 py-5">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 font-display text-xl font-bold text-[#0B3BA8]"><img src="/logo-icon.png" alt="" className="h-9 w-9 object-contain" />Kreditofacile</Link>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-500 hidden sm:inline">{user?.email}</span>
          {ok && <Link to="/" data-testid="go-dashboard" className="rounded-full bg-[#0F172A] text-white px-4 py-2">Vai al CRM</Link>}
          <button data-testid="subscribe-logout" onClick={logout} className="flex items-center gap-1.5 text-slate-500 hover:text-slate-900"><LogOut className="h-4 w-4" />Esci</button>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-6 py-10 space-y-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{ok ? "Il tuo abbonamento" : lapsed ? "Riattiva il tuo abbonamento" : `Benvenuto${user?.name ? `, ${user.name.split(" ")[0]}` : ""}!`}</h1>
          <p className="text-slate-500 mt-2">
            {st.access === "admin" ? "Sei l'amministratore: accesso completo senza abbonamento." : ok ? "Gestisci piano, metodo di pagamento e fatture." : lapsed ? `I tuoi dati sono al sicuro. ${st.access === "readonly" ? `Sola lettura fino al ${fmt(st.readonly_until)}.` : "L'accesso è sospeso finché non riattivi."}` : "Scegli il piano e inizia: 30 giorni gratis, tutte le funzioni incluse."}
          </p>
        </div>
        {st.access === "admin" ? (
          <div className="space-y-4">
            <p className="flex items-center gap-2 text-sm text-emerald-700"><ShieldCheck className="h-4 w-4" />Account amministratore</p>
            <Link to="/admin" data-testid="go-admin-panel" className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] text-white px-5 py-2.5 text-sm font-medium hover:bg-sky-600 transition-colors"><ShieldCheck className="h-4 w-4" />Apri il pannello Admin</Link>
          </div>
        )
          : ok ? <Manage st={st} /> : <PlanPicker st={st} onDone={() => { refetch(); refresh(); }} />}
      </main>
    </div>
  );
}
